import { inject } from '@angular/core'

import { switchMap } from 'rxjs'

import { ImportProjectStore } from '@flowx/designer/modules/projects/components/projects/import-project.store'
import { WorkspacesAndPermissionsStore } from '@flowx/designer/modules/workspaces/workspaces-and-permissions.store'
import { FlxNavigationService } from '@flowx/designer/shared/services/navigation.service'
import { loadLocalFile } from '@flowx/shared/utils'

import {
  ExtendedNotification,
  NotificationActionHandler,
  NotificationActionType,
} from '../notifications.model'

type BuildImportActionType =
  NotificationActionType.VIEW_BUILD_IMPORT | NotificationActionType.RETRY_BUILD_IMPORT

/**
 * The actions a build import notification offers, the build counterparts of the app version import's.
 * Must be called in an injection context, for the same reason as `injectAppVersionImportHandlers`.
 */
export const injectBuildImportHandlers = (): Record<
  BuildImportActionType,
  NotificationActionHandler
> => {
  const navigationService = inject(FlxNavigationService)
  const importProjectStore = inject(ImportProjectStore)
  const workspacesAndPermissionsStore = inject(WorkspacesAndPermissionsStore)

  // Opens the imported project's builds list, as the Projects page used to once an import finished;
  // a library's included.
  const viewImport = (notification: ExtendedNotification): void => {
    navigationService.navigateToWorkspaceBuilds(notification.workspaceId, notification.appId)
  }

  // Asks for the archive again, since a browser cannot re-select a file, and imports it into the
  // workspace the failed run was in, wherever the user is now; the new run reports through a
  // notification of its own.
  const retryImport = (notification: ExtendedNotification): void => {
    loadLocalFile({ accept: ['.zip'] })
      .pipe(
        switchMap((files) =>
          importProjectStore.importBuild(
            workspacesAndPermissionsStore.casUserId(),
            files[0],
            notification.workspaceId
          )
        )
      )
      .subscribe()
  }

  return {
    [NotificationActionType.VIEW_BUILD_IMPORT]: viewImport,
    [NotificationActionType.RETRY_BUILD_IMPORT]: retryImport,
  }
}
